import axios, {AxiosResponse, ResponseType} from 'axios';
import { AwsClient } from 'aws4fetch';
import {
    LOCAL_S3_ACCESS_KEY,
    LOCAL_S3_SECRET_KEY,
    LOCAL_S3_REGION,
    LOCAL_S3_BASE_URL,
    LOCAL_S3_ENABLED
} from '@/config/localS3';
import {
    /* saveJourney,
    saveJourneySet,
    saveMedia,*/
    JourneySetToDisplay, saveJourneySetAndData, parseEncryptedArray, applyManifestUpdate,
} from '@/storage/DataStorage';
/* import { v4 as uuidv4 } from "uuid";*/
import {createLoadSpinner} from "@/services/ShareService";
import {store} from "@/store";
import {FileIdentifiers, MimeTypes} from "@/types/types";
import {logInfo, writeFile} from "@/utilities/Filesystem";

const USERNAME = "generalUser123";
const PASSWORD = "U8CGKbJJa9e64mGt";

function fetchWithTimeout(promise: Promise<any>, ms: number) {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Request timed out")), ms)
  );
  return Promise.race([promise, timeout]);
}

// Never combine this with axios's own `timeout` option.
// The AbortController will enforce the timeout at ANY stage.
export async function withAbortTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
    ms: number
    ): Promise<T> {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(new DOMException('Timeout', 'AbortError')), ms);
    try {
        return await fn(controller.signal);
    } finally {
        clearTimeout(id);
    }
}

export async function hasInternet(ms = 5000): Promise<'ok' | 'captive' | 'offline' | 'timeout'> {
  if (!store.getters.allowsInternetAccess()) {
    return 'offline';
  }
  const probe = `https://1.1.1.1/cdn-cgi/trace`;
  //console.log(`[hasInternet]Offline check to ${probe} with timeout ${ms}ms`);
  try {
    const res = await withAbortTimeout(
      (signal) => fetch(probe, { method: 'GET', cache: 'no-store', signal }),
      ms
    );
    // 204 means open internet; anything else could be captive or filtered
    //console.log(`[hasInternet]Offline check response: ${res.status}`);
    if (res.ok) return 'ok';
    return 'captive';
  } catch (e: any) {
    //console.log('[hasInternet]Offline check failed', e.message);
    if (e?.name === 'AbortError') return 'timeout';
    return 'offline';
  }
}

export async function getJourneySetMetadata(): Promise<any> {
    const messages = await store.getters.getLocaleMessages();

    if (!store.getters.allowsInternetAccess()) {
        const { dataDirectoryFileExists, readJSONFileFromDataDirectory } = await import('@/utilities/Filesystem');
        if (await dataDirectoryFileExists('journeySetMetadata.json')) {
            const cachedData = await readJSONFileFromDataDirectory('journeySetMetadata.json', false);
            if (cachedData && Object.keys(cachedData).length > 0) {
                return cachedData;
            }
        }
        return [];
    }
    
    // Check if we have a cached version first (non-web platforms)
    const Device = (await import('@capacitor/device')).Device;
    const deviceInfo = await Device.getInfo();
    
    if (deviceInfo.platform !== 'web') {
        try {
            const { dataDirectoryFileExists, readJSONFileFromDataDirectory } = await import('@/utilities/Filesystem');
            const cacheExists = await dataDirectoryFileExists('journeySetMetadata.json');
            
            if (cacheExists) {
                // Try to use cached version first, then update in background
                const cachedData = await readJSONFileFromDataDirectory('journeySetMetadata.json', false);
                if (cachedData && Object.keys(cachedData).length > 0) {
                    logInfo('[getJourneySetMetadata] Using cached manifest, will check for updates in background', 1);
                    
                    // Schedule background update (fire and forget)
                    updateJourneySetMetadataInBackground(messages);
                    
                    return cachedData;
                }
            }
        } catch (error) {
            logInfo('[getJourneySetMetadata] Error reading cache, will download from S3', 1);
        }
    }
    
    // No cache or web platform - download from S3
    const metadata = await getFromS3(
        messages.language.variant,
        `journeySetMetadata_encrypted.json?timestamp=${new Date().getTime()}`,
        'json'
    ).catch(function (){
        //console.log('No journey setmetadata from AWS');
        return undefined;
    });
    
    const manifestData = metadata?.data ?? [];
    if (store.getters.isPwa()) {
        const parsed = Array.isArray(manifestData)
            ? manifestData
            : parseEncryptedArray(manifestData.content);
        await applyManifestUpdate(parsed);
        return manifestData;
    }
    
    // Cache it for next time (non-web platforms)
    if (deviceInfo.platform !== 'web' && manifestData && Object.keys(manifestData).length > 0) {
        try {
            const { writeFileToDataDirectory } = await import('@/utilities/Filesystem');
            await writeFileToDataDirectory('journeySetMetadata.json', JSON.stringify(manifestData));
            logInfo('[getJourneySetMetadata] Cached manifest locally', 2);
        } catch (error) {
            logInfo('[getJourneySetMetadata] Error caching manifest', 2);
        }
    }
    
    return manifestData;
}

// Background update function (non-blocking)
async function updateJourneySetMetadataInBackground(messages: any) {
    if (!store.getters.allowsInternetAccess()) {
        return;
    }
    try {
        logInfo('[updateJourneySetMetadataInBackground] Starting background update of manifest', 2);
        
        const metadata = await getFromS3(
            messages.language.variant,
            `journeySetMetadata_encrypted.json?timestamp=${new Date().getTime()}`,
            'json',
            false,
            30000
        ).catch(function (){
            return undefined;
        });
        
        if (metadata?.data && Object.keys(metadata.data).length > 0) {
            if (store.getters.isPwa()) {
                const manifestData = Array.isArray(metadata.data)
                    ? metadata.data
                    : parseEncryptedArray(metadata.data.content);
                await applyManifestUpdate(manifestData);
                logInfo('[updateJourneySetMetadataInBackground] Background manifest update completed (web)', 1);
                return;
            }
            const { writeFileToDataDirectory } = await import('@/utilities/Filesystem');
            await writeFileToDataDirectory('journeySetMetadata.json', JSON.stringify(metadata.data));
            logInfo('[updateJourneySetMetadataInBackground] Background manifest update completed', 1);
        }
    } catch (error) {
        logInfo('[updateJourneySetMetadataInBackground] Background update failed (non-critical)', 2);
    }
}

export function getAWSUrl(rootFolder: string, key: string, useHost = false): string {
    if (isLocalS3()) {
        const base = LOCAL_S3_BASE_URL;
        if (!rootFolder || rootFolder === 'demopattern' || rootFolder === 'discoverystudies') {
            return `${base}/${key}`;
        }
        return `${base}/${rootFolder}/${key}`;
    }
    let fetchUrl = '';
    let host = '';
    if (store.getters.isPwa()) {
        host = window.location.hostname;
        if (host === "localhost") {
            fetchUrl = `https://testdemopattern.net/${rootFolder}/${key}`;
        }
        else if (useHost) {
            const hostArray = host.split(".");
            hostArray.shift();
            fetchUrl = `https://${ hostArray.join(".") }/${ rootFolder }/${ key }`;
        }
    }
    if (!host || (host !== 'localhost' && !useHost)){
        fetchUrl = `https://${ store.state.downloadDomain }/${ rootFolder }/${ key }`;
    }
    
    return fetchUrl;
}

export function isLocalS3Enabled(): boolean {
    const env = process.env || {};
    return Boolean(env.VUE_APP_LOCAL_S3_ENDPOINT || env.VUE_APP_LOCAL_S3_HOST_IP);
}

function isLocalS3(): boolean {
    if (isLocalS3Enabled()) {
        return true;
    }
    if (typeof window === 'undefined') {
        return false;
    }
    const host = window.location.hostname;
    return LOCAL_S3_ENABLED && (host === 'localhost' || host === '127.0.0.1');
}

export async function getPresignedLocalS3Url(
    rootFolder: string,
    key: string,
    expiresSeconds = 900
): Promise<string> {
    const baseUrl = getAWSUrl(rootFolder, key);
    if (!isLocalS3()) {
        return baseUrl;
    }
    try {
        const client = new AwsClient({
            accessKeyId: LOCAL_S3_ACCESS_KEY,
            secretAccessKey: LOCAL_S3_SECRET_KEY,
            service: "s3",
            region: LOCAL_S3_REGION
        });
        const signedRequest = await client.sign(
            new Request(baseUrl, { method: "GET" }),
            { signQuery: true, expires: expiresSeconds } as any
        );
        return signedRequest.url;
    } catch (error: any) {
        logInfo(`[getPresignedLocalS3Url] Failed to sign URL: ${error?.message || error}`, 1);
        return baseUrl;
    }
}

export async function getBundleDownloadUrl(bundleName: string): Promise<string> {
    if (!isLocalS3()) {
        return `https://${store.state.downloadDomain}/updates/${bundleName}`;
    }
    return await getPresignedLocalS3Url('updates', bundleName);
}

export async function getFromS3(rootFolder: string, key: string, responseType: ResponseType = 'json', useHost = false, customTimeout?: number): Promise<AxiosResponse> {
    if (!store.getters.allowsInternetAccess()) {
        logInfo(`[getFromS3] Blocked (offline-only secure): ${rootFolder}/${key}`, 1);
        store.dispatch("setIsInvalidUrl", false);
        return { data: responseType === 'json' ? {} : '' } as AxiosResponse;
    }
    logInfo(`[getFromS3] Fetching ${rootFolder}/${key} from S3` , 2);
    const fetchUrl = getAWSUrl(rootFolder, key, useHost);
    console.log(`[getFromS3] Fetching URL: ${fetchUrl}`);
    logInfo(`[getFromS3] Fetching URL: ${fetchUrl}`,0);
    
    // Use custom timeout if provided, otherwise default to 30000
    const timeoutMs = customTimeout !== undefined ? customTimeout : 30000;
    
    if (customTimeout !== undefined) {
        logInfo(`[getFromS3] Using dynamic timeout: ${timeoutMs}ms`, 1);
    }
    
    store.dispatch("setIsInvalidUrl", false);
    if (isLocalS3()) {
        return await getFromLocalS3(fetchUrl, responseType, timeoutMs);
    }
    const config: any = {
        responseType : responseType,
        timeout: timeoutMs
    };
    config.auth = {
        username: USERNAME,
        password: PASSWORD,
    };
    return await axios.get(fetchUrl, config).catch(function (e: any) {
        console.log(`[getFromS3] Error: ${e.message} `);
        logInfo(`[getFromS3] Error details - message: ${e.message}, code: ${e.code}, response status: ${e.response?.status}, response data: ${e.response?.data}`, 0);
        if (e.response && (e.response.status === 403 || e.response.status === 404)) {
            store.dispatch("setIsInvalidUrl", true);
        } else {
            store.dispatch("setIsInvalidUrl", false);
        }
        return {data: responseType === 'json' ? {} : ''} as AxiosResponse;
    });
}

async function getFromLocalS3(fetchUrl: string, responseType: ResponseType, timeoutMs: number): Promise<AxiosResponse> {
    try {
        const client = new AwsClient({
            accessKeyId: LOCAL_S3_ACCESS_KEY,
            secretAccessKey: LOCAL_S3_SECRET_KEY,
            service: "s3",
            region: LOCAL_S3_REGION
        });
        const response = await withAbortTimeout(
            (signal) => client.fetch(fetchUrl, { method: "GET", signal }),
            timeoutMs
        );
        if (!response.ok) {
            store.dispatch("setIsInvalidUrl", response.status === 403 || response.status === 404);
            return { data: responseType === 'json' ? {} : '' } as AxiosResponse;
        }
        store.dispatch("setIsInvalidUrl", false);
        if (responseType === 'text') {
            return { data: await response.text() } as AxiosResponse;
        }
        if (responseType === 'blob') {
            return { data: await response.blob() } as AxiosResponse;
        }
        return { data: await response.json() } as AxiosResponse;
    } catch (error: any) {
        logInfo(`[getFromLocalS3] Error: ${error?.message || error}`, 0);
        store.dispatch("setIsInvalidUrl", true);
        return { data: responseType === 'json' ? {} : '' } as AxiosResponse;
    }
}
// Only journeysets ingested through manifest file updates should be marked as isDefault as it is used to drive background loading


function isDefault(journeySet: any) {
    if ((store.getters.isPwa() && journeySet['IsPwaDefault']) ||
        (store.getters.isSecure() && journeySet['IsSecureDefault']) ||
        (store.getters.isOnlineApp() && journeySet['IsAppDefault'])) {
        return true;
    }
    
    return false;
}

async function storeDownloadedJourneySet(journeySet: JourneySetToDisplay, quadrantId: string) {
    journeySet.isDefaultSet = isDefault(journeySet);
    journeySet.quadrantId = quadrantId;
    journeySet.numberOfJourneys = journeySet.journeys?.length || 0;
    journeySet.alreadyDownloaded = true;

    // Secure app: allow resource downloads from S3 for this user-initiated download.
    // loadResourceFile() otherwise uses shouldUseAssetResources() and only reads from bundled assets.
    const wasOverride = store.state.secureUpdateOverride;
    if (store.getters.isSecure() && !wasOverride) {
        store.dispatch('setSecureUpdateOverride', true);
    }
    try {
        await saveJourneySetAndData(journeySet);
    } finally {
        if (store.getters.isSecure() && !wasOverride) {
            store.dispatch('setSecureUpdateOverride', false);
        }
    }
}

export async function downloadJourneySet  (journeySetId: string, quadrantId: string) {
    try {
        const messages = await store.getters.getLocaleMessages();
        const content = await getFromS3(messages.language.variant, `journeySet_${journeySetId.toUpperCase()}.json?timestamp=${new Date().getTime()}`);
        const { data: journeySet }: { data: JourneySetToDisplay } = content;
        if(!journeySet || !journeySet.id){
            logInfo(`[downloadJourneySet] Invalid journey set payload for ${journeySetId}`, 0);
            store.dispatch("setIsInvalidUrl", true);
            return;
        }
        if(!store.state.isInvalidUrl){
            await storeDownloadedJourneySet(journeySet, quadrantId);
        }
    }
    catch {
        return;
    }
}

export async function downloadJourneySetWithSpinner(journeySetId: string, quadrantId: string) {
    if (!store.getters.allowsInternetAccess()) {
        return;
    }
    const loading = await createLoadSpinner();
    try {
        await loading.present();
        await downloadJourneySet(journeySetId, quadrantId);
    } finally {
        await loading.dismiss();
    }
}

export async function downloadAPK(appStoreVersion = false) {
    if (!store.getters.allowsInternetAccess()) {
        return;
    }
    try {
        const messages = await store.getters.getLocaleMessages();
        let apkName = '';
        if (appStoreVersion) {
            apkName = `app_${ messages.language.variant.replace(/-/g, "") }_appStore.apk`;
        } else if (process.env.NODE_ENV === 'development') {
            apkName = `app_${ messages.language.variant.replace(/-/g, "") }_admin.apk`;
        } else {
            apkName = `${ FileIdentifiers.Apk }${ messages.application.appName }.apk`;
        }
        const apk = await getFromS3(messages.language.variant, apkName + `?timestamp=${new Date().getTime()}` , 'blob');
        const apkExportName = `${ messages.application.appName }${ FileIdentifiers.Apk }.apk`;

        const reader = new FileReader();
        await new Promise((resolve) => {
            reader.onloadend = async () => {
                const result = reader.result as string;
                const base64Data = result.split(',')[1];
                await writeFile(apkExportName, base64Data, false, MimeTypes.APK, false);
                resolve(true);
            };
            reader.readAsDataURL(apk.data);
        });
    } catch {
        return;
    }
}

/**
 * Converts a blob to base64 string
 * @param blob - The blob data to convert
 * @param logMessage - Optional log message for successful conversion
 * @returns Promise<string> - Base64 string representation of the blob
 */
function convertBlobToBase64(blob: Blob, logMessage?: string): Promise<string> {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
        reader.onloadend = () => {
            try {
                const result = reader.result as string;
                const base64Data = result.split(',')[1];
                if (logMessage) {
                    logInfo(logMessage, 0);
                }
                resolve(base64Data);
            } catch (error) {
                console.error('[downloadSignatureAsBase64] Error converting binary signature to base64:', error);
                logInfo(`[downloadSignatureAsBase64] Error converting binary signature to base64: ${error}`, 1);
                reject(error);
            }
        };
        
        reader.onerror = () => {
            const error = new Error('Failed to read binary signature as base64');
            console.error('[downloadSignatureAsBase64]', error.message);
            logInfo(`[downloadSignatureAsBase64] ${error.message}`, 1);
            reject(error);
        };
        
        reader.readAsDataURL(blob);
    });
}

/**
 * Downloads a signature file from S3. If the file is binary (e.g., .sig files), it's converted to a base64 string.
 * If the file is already a base64-encoded text file, its content is returned directly.
 * Currently hardcoded to use production bucket (multiplyingdownloads.com) for testing.
 * @param rootFolder - The S3 root folder path (ignored, always uses "updates")
 * @param signatureFileName - The name of the signature file to download
 * @param useHost - Whether to use the host-based URL construction (ignored, always uses production)
 * @returns Promise<string | null> - Base64 string of the signature file or null if error
 */
export async function downloadSignatureAsBase64(
    rootFolder: string, 
    signatureFileName: string, 
    useHost = false,
    timeoutMs = 10000
): Promise<string | null> {
    try {
        if (!store.getters.allowsInternetAccess()) {
            return null;
        }
        const signatureKey = `${signatureFileName}?timestamp=${new Date().getTime()}`;
        const signatureUrl = isLocalS3()
            ? getAWSUrl(rootFolder, signatureKey, useHost)
            : `https://multiplyingdownloads.com/${rootFolder}/${signatureFileName}?timestamp=${new Date().getTime()}`;
        logInfo(`[downloadSignatureAsBase64] Downloading signature: ${signatureUrl}`, 0);
        
        // Check if file is a .sig file (typically binary) - download directly as blob
        const isSigFile = signatureFileName.toLowerCase().endsWith('.sig');
        
        if (isSigFile) {
            logInfo('[downloadSignatureAsBase64] Detected .sig file, downloading as binary blob', 0);
            if (isLocalS3()) {
                const localResponse = await getFromS3(rootFolder, signatureKey, 'blob', useHost, timeoutMs);
                if (!localResponse.data) {
                    logInfo('[downloadSignatureAsBase64] Failed to download signature file as blob (local S3)', 0);
                    return null;
                }
                return await convertBlobToBase64(
                    localResponse.data, 
                    '[downloadSignatureAsBase64] Successfully converted binary .sig file to base64'
                );
            } else {
                // Download directly as blob for binary .sig files
                const blobResponse = await axios.get(signatureUrl, {
                    responseType: 'blob',
                    auth: {
                        username: USERNAME,
                        password: PASSWORD,
                    },
                    timeout: timeoutMs
                }).catch(function (e: any) {
                    console.log(`[downloadSignatureAsBase64] Error: ${e.message} `);
                    logInfo(`[downloadSignatureAsBase64] Error details - message: ${e.message}, code: ${e.code}, response status: ${e.response?.status}`, 0);
                    return {data: null} as AxiosResponse;
                });

                if (!blobResponse.data) {
                    logInfo('[downloadSignatureAsBase64] Failed to download signature file as blob', 0);
                    return null;
                }

                return await convertBlobToBase64(
                    blobResponse.data, 
                    '[downloadSignatureAsBase64] Successfully converted binary .sig file to base64'
                );
            }
        }
        
        // For non-.sig files, try text first to check if it's already base64
        const response = isLocalS3()
            ? await getFromS3(rootFolder, signatureKey, 'text', useHost, timeoutMs)
            : await axios.get(signatureUrl, {
                responseType: 'text',
                auth: {
                    username: USERNAME,
                    password: PASSWORD,
                },
                timeout: timeoutMs
            }).catch(function (e: any) {
                console.log(`[downloadSignatureAsBase64] Error: ${e.message} `);
                logInfo(`[downloadSignatureAsBase64] Error details - message: ${e.message}, code: ${e.code}, response status: ${e.response?.status}`, 0);
                return {data: null} as AxiosResponse;
            });
        
        // Check if the download was successful
        if (!response.data) {
            logInfo('[downloadSignatureAsBase64] Failed to download signature file',0);
            return null;
        }

        const signatureContent = response.data.trim();
        
        // Basic check to see if the content is likely base64
        // This regex checks for valid base64 characters and padding
        const isBase64 = /^[A-Za-z0-9+/]+={0,2}$/.test(signatureContent);

        if (isBase64) {
            logInfo('[downloadSignatureAsBase64] Signature file is already base64-encoded.',0);
            return signatureContent;
        } else {
            logInfo('[downloadSignatureAsBase64] Signature file is binary, converting to base64.',0);
            // If not base64, assume it's a binary blob and convert it
            const blobResponse = isLocalS3()
                ? await getFromS3(rootFolder, signatureKey, 'blob', useHost, timeoutMs)
                : await axios.get(signatureUrl, {
                    responseType: 'blob',
                    auth: {
                        username: USERNAME,
                        password: PASSWORD,
                    },
                    timeout: timeoutMs
                }).catch(function (e: any) {
                    console.log(`[downloadSignatureAsBase64] Error: ${e.message} `);
                    logInfo(`[downloadSignatureAsBase64] Error details - message: ${e.message}, code: ${e.code}, response status: ${e.response?.status}`, 0);
                    return {data: null} as AxiosResponse;
                });

            if (!blobResponse.data) {
                logInfo('[downloadSignatureAsBase64] Failed to download signature file as blob.',0);
                return null;
            }

            return await convertBlobToBase64(
                blobResponse.data,
                '[downloadSignatureAsBase64] Successfully converted binary signature to base64'
            );
        }
        
    } catch (error: any) {
        console.error('[downloadSignatureAsBase64] Error downloading signature file:', error);
        logInfo(`[downloadSignatureAsBase64] Error downloading signature file: ${error}`, 0);
        return null;
    }
}
